import { useEffect, useState } from 'react'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from 'react-router'
import Alert from '@cloudscape-design/components/alert'
import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import Checkbox from '@cloudscape-design/components/checkbox'
import FormField from '@cloudscape-design/components/form-field'
import Input from '@cloudscape-design/components/input'
import Link from '@cloudscape-design/components/link'
import SpaceBetween from '@cloudscape-design/components/space-between'
import {
  createUserWithEmail,
  setAuthPersistence,
  signInWithEmail,
  signInWithFacebook,
  signInWithGoogle,
} from '@/clients/firebase'
import { loginSchema, registerSchema, type LoginInput, type RegisterInput } from '@/lib/schemas'

// ── Brand tokens ────────────────────────────────────────────
const B = {
  bg:            '#09090b',
  cardBg:        '#111113',
  border:        '#1e1e22',
  emerald:       '#10B981',
  teal:          '#14B8A6',
  textPrimary:   '#F0FDF4',
  textSecondary: '#a1a1aa',
  textMuted:     '#71717a',
} as const

const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"

const GOOGLE_ICON = `data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgMTggMTgiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTTE3LjY0IDkuMjA1YzAtLjYzOS0uMDU3LTEuMjUyLS4xNjQtMS44NDFIOXYzLjQ4MWg0Ljg0NGE0LjE0IDQuMTQgMCAwIDEtMS43OTYgMi43MTZ2Mi4yNThoMi45MDhjMS43MDItMS41NjcgMi42ODQtMy44NzQgMi42ODQtNi42MTR6IiBmaWxsPSIjNDI4NUY0Ii8+PHBhdGggZD0iTTkgMThjMi40MyAwIDQuNDY3LS44MDYgNS45NTYtMi4xOGwtMi45MDgtMi4yNTljLS44MDYuNTQtMS44MzcuODYtMy4wNDguODYtMi4zNDQgMC00LjMyOC0xLjU4My01LjAzNi0zLjcxSDEuMDE3djIuMzMyQTguOTk5IDguOTk5IDAgMCAwIDkgMTh6IiBmaWxsPSIjMzRBODUzIi8+PHBhdGggZD0iTTMuOTY0IDEwLjcxYTUuNDEgNS40MSAwIDAgMS0uMjgxLTEuNzFjMC0uNTk0LjEwMi0xLjE3LjI4MS0xLjcxVjQuOTU4SDEuMDE3QTkuMDA5IDkuMDA5IDAgMCAwIDAgOWMwIDEuNDUyLjM0OCAyLjgyNy45NiA0LjA0MmwyLjk5Ni0yLjMzMnoiIGZpbGw9IiNGQkJDMDUiLz48cGF0aCBkPSJNOSAzLjU4YzEuMzIxIDAgMi41MDguNDU0IDMuNDQgMS4zNDVsMi41ODItMi41OEMxMy40NjMuODkxIDExLjQyNyAwIDkgMGE4Ljk5OSA4Ljk5OSAwIDAgMC03Ljk4MyA0Ljk1OGwyLjk5NiAyLjMzMkM0LjY3MiA1LjE2MyA2LjY1NiAzLjU4IDkgMy41OHoiIGZpbGw9IiNFQTM1MzUiLz48L3N2Zz4=`
const FACEBOOK_ICON = `data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgMjQgMjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTTI0IDEyLjA3M2MwLTYuNjI3LTUuMzczLTEyLTEyLTEycy0xMiA1LjM3My0xMiAxMmMwIDUuOTkgNC4zODggMTAuOTU0IDEwLjEyNSAxMS44NTR2LTguMzg1SDcuMDc4di0zLjQ3aDMuMDQ3VjkuNDNjMC0zLjAwNyAxLjc5Mi00LjY2OSA0LjUzMy00LjY2OSAxLjMxMiAwIDIuNjg2LjIzNSAyLjY4Ni4yMzV2Mi45NTNIMTUuODNjLTEuNDkxIDAtMS45NTYuOTI1LTEuOTU2IDEuODc0djIuMjVoMy4zMjhsLS41MzIgMy40N2gtMi43OTZ2OC4zODVDMTkuNjEyIDIzLjAyNyAyNCAxOC4wNjIgMjQgMTIuMDczeiIgZmlsbD0iIzE4NzdGMiIvPjwvc3ZnPg==`

const CHART_BARS = [38, 55, 44, 70, 62, 85, 74, 92, 80, 96, 88, 100]

// Shared auth form styles — applied to [data-form-panel]
const AUTH_FORM_CSS = `
  /* Social buttons — pill, outlined neutral */
  [data-form-panel] button[class*="variant-normal"] {
    border-color: #dee2e6 !important;
    color: #1a1a1a !important;
    border-radius: 50px !important;
    min-height: 48px !important;
    background: #ffffff !important;
    font-weight: 500 !important;
    font-size: 0.875rem !important;
  }
  [data-form-panel] button[class*="variant-normal"]:hover {
    background-color: #f8f9fa !important;
    border-color: #adb5bd !important;
    color: #1a1a1a !important;
  }
  /* Primary button — pill, emerald→teal gradient */
  [data-form-panel] button[class*="variant-primary"] {
    background: linear-gradient(135deg, #10B981, #14B8A6) !important;
    border-color: #10B981 !important;
    border-radius: 50px !important;
    color: #ffffff !important;
    min-height: 52px !important;
    font-weight: 600 !important;
    font-size: 0.95rem !important;
    box-shadow: 0 0 16px rgba(16,185,129,0.2) !important;
  }
  [data-form-panel] button[class*="variant-primary"]:hover {
    background: linear-gradient(135deg, #0D9373, #0B8A80) !important;
    border-color: #0D9373 !important;
    box-shadow: 0 0 24px rgba(16,185,129,0.35) !important;
  }
  [data-form-panel] button[class*="variant-primary"]:active {
    background: linear-gradient(135deg, #0B7A5E, #0B7A5E) !important;
    border-color: #0B7A5E !important;
    box-shadow: none !important;
  }
  /* Input fields — rounded, no shadow */
  [data-form-panel] [class*="input-container"],
  [data-form-panel] [class*="input-container"] *,
  [data-form-panel] input {
    border-radius: 8px !important;
  }
  [data-form-panel] [class*="input-container"] {
    border-color: #dee2e6 !important;
    background: #ffffff !important;
    min-height: 48px !important;
  }
  [data-form-panel] [class*="input-container"]:focus-within {
    border-color: #14B8A6 !important;
    background: #ffffff !important;
    box-shadow: 0 0 0 3px rgba(20,184,166,0.15) !important;
  }
  /* Links */
  [data-form-panel] a[class*="link"] {
    color: #14B8A6 !important;
  }
  [data-form-panel] a[class*="link"]:hover {
    color: #0D9373 !important;
  }
  /* Fix browser autofill dark background override */
  [data-form-panel] input:-webkit-autofill,
  [data-form-panel] input:-webkit-autofill:hover,
  [data-form-panel] input:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0 1000px #ffffff inset !important;
    -webkit-text-fill-color: #0a0a0a !important;
    caret-color: #0a0a0a !important;
  }
  /* Prevent overscroll bounce on short mobile viewports */
  [data-form-panel] {
    overscroll-behavior: contain;
  }
`

// ── Login form ──────────────────────────────────────────────
function LoginForm({
  onSwitchMode,
  error,
  setError,
  isMobile,
}: {
  onSwitchMode: () => void
  error: string | null
  setError: (e: string | null) => void
  isMobile?: boolean
}) {
  const [googleLoading, setGoogleLoading] = useState(false)
  const [facebookLoading, setFacebookLoading] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  })

  const socialLoading = googleLoading || facebookLoading

  const handleGoogleSignIn = async () => {
    setError(null)
    setGoogleLoading(true)
    try {
      await signInWithGoogle()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Google sign-in failed')
    } finally {
      setGoogleLoading(false)
    }
  }

  const handleFacebookSignIn = async () => {
    setError(null)
    setFacebookLoading(true)
    try {
      await signInWithFacebook()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Facebook sign-in failed')
    } finally {
      setFacebookLoading(false)
    }
  }

  const onSubmit = async (values: LoginInput) => {
    setError(null)
    try {
      await setAuthPersistence(rememberMe)
      await signInWithEmail(values.email, values.password)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed')
    }
  }

  return (
    <SpaceBetween direction="vertical" size="l">
      {/* Heading */}
      <SpaceBetween direction="vertical" size="xs">
        <Box
          tagOverride="h1"
          nativeAttributes={{
            style: { fontFamily: FONT, fontSize: isMobile ? '1.75rem' : '2.25rem', fontWeight: 700, letterSpacing: '-0.03em', color: '#0a0a0a', margin: 0, lineHeight: 1.15 },
          }}
        >
          Get Started Now
        </Box>
        <Box
          tagOverride="p"
          nativeAttributes={{ style: { fontFamily: FONT, fontSize: '0.9rem', color: '#71717a', margin: 0 } }}
        >
          Enter your credentials to access your account
        </Box>
      </SpaceBetween>

      {error !== null && (
        <Alert type="error" dismissible onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Social buttons — 2 columns */}
      <Box
        tagOverride="div"
        nativeAttributes={{ style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' } }}
      >
        <Button
          variant="normal"
          fullWidth
          iconUrl={GOOGLE_ICON}
          iconAlt="Google"
          loading={googleLoading}
          disabled={facebookLoading || isSubmitting}
          onClick={() => { void handleGoogleSignIn() }}
        >
          Google
        </Button>
        <Button
          variant="normal"
          fullWidth
          iconUrl={FACEBOOK_ICON}
          iconAlt="Facebook"
          loading={facebookLoading}
          disabled={googleLoading || isSubmitting}
          onClick={() => { void handleFacebookSignIn() }}
        >
          Facebook
        </Button>
      </Box>

      {/* OR divider */}
      <Box tagOverride="div" nativeAttributes={{ style: { display: 'flex', alignItems: 'center', gap: '12px' } }}>
        <Box tagOverride="div" nativeAttributes={{ style: { flex: 1, height: '1px', background: '#e5e5e5' } }} />
        <Box
          tagOverride="span"
          nativeAttributes={{ style: { fontFamily: FONT, fontSize: '0.75rem', color: '#a3a3a3', fontWeight: 500, letterSpacing: '0.06em' } }}
        >
          or
        </Box>
        <Box tagOverride="div" nativeAttributes={{ style: { flex: 1, height: '1px', background: '#e5e5e5' } }} />
      </Box>

      {/* Email form */}
      <SpaceBetween direction="vertical" size="s">
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <FormField label="Email" errorText={errors.email?.message}>
              <Input
                type="email"
                value={field.value}
                onChange={({ detail }) => field.onChange(detail.value)}
                placeholder="you@agency.com"
              />
            </FormField>
          )}
        />
        <Controller
          name="password"
          control={control}
          render={({ field }) => (
            <FormField
              label={
                <Box tagOverride="span" nativeAttributes={{ style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' } }}>
                  <Box tagOverride="span" nativeAttributes={{ style: { fontFamily: FONT } }}>Password</Box>
                  <Link href="#">Forgot password?</Link>
                </Box>
              }
              errorText={errors.password?.message}
            >
              <Input
                type="password"
                ariaLabel="Password"
                value={field.value}
                onChange={({ detail }) => field.onChange(detail.value)}
                placeholder="••••••••"
              />
            </FormField>
          )}
        />
        <Checkbox
          checked={rememberMe}
          onChange={({ detail }) => setRememberMe(detail.checked)}
        >
          Remember me
        </Checkbox>
        <Button
          variant="primary"
          fullWidth
          loading={isSubmitting}
          disabled={socialLoading}
          onClick={() => { handleSubmit(onSubmit)().catch(() => undefined) }}
        >
          Sign in with Email
        </Button>
      </SpaceBetween>

      {/* Sign-up link */}
      <Box tagOverride="div" nativeAttributes={{ style: { textAlign: 'center' } }}>
        <Box
          tagOverride="span"
          nativeAttributes={{ style: { fontFamily: FONT, fontSize: '0.875rem', color: '#71717a' } }}
        >
          Don't have an account?{' '}
        </Box>
        <Link onFollow={onSwitchMode}>Create one</Link>
      </Box>
    </SpaceBetween>
  )
}

// ── Register form ───────────────────────────────────────────
function RegisterForm({
  onSwitchMode,
  error,
  setError,
  isMobile,
}: {
  onSwitchMode: () => void
  error: string | null
  setError: (e: string | null) => void
  isMobile?: boolean
}) {
  const navigate = useNavigate()
  const [googleLoading, setGoogleLoading] = useState(false)
  const [facebookLoading, setFacebookLoading] = useState(false)
  const [agreedToTerms, setAgreedToTerms] = useState(false)

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterInput>({
    resolver: zodResolver(registerSchema),
    defaultValues: { name: '', email: '', password: '', confirmPassword: '' },
  })

  const socialLoading = googleLoading || facebookLoading

  const handleGoogleSignIn = async () => {
    setError(null)
    setGoogleLoading(true)
    try {
      await signInWithGoogle()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Google sign-in failed')
    } finally {
      setGoogleLoading(false)
    }
  }

  const handleFacebookSignIn = async () => {
    setError(null)
    setFacebookLoading(true)
    try {
      await signInWithFacebook()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Facebook sign-in failed')
    } finally {
      setFacebookLoading(false)
    }
  }

  const onSubmit = async (values: RegisterInput) => {
    setError(null)
    try {
      await createUserWithEmail(values.email, values.password, values.name)
      navigate('/status')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed')
    }
  }

  return (
    <SpaceBetween direction="vertical" size="l">
      {/* Heading */}
      <SpaceBetween direction="vertical" size="xs">
        <Box
          tagOverride="h1"
          nativeAttributes={{
            style: { fontFamily: FONT, fontSize: isMobile ? '1.75rem' : '2rem', fontWeight: 700, letterSpacing: '-0.03em', color: '#0a0a0a', margin: 0, lineHeight: 1.15 },
          }}
        >
          Create account
        </Box>
        <Box
          tagOverride="p"
          nativeAttributes={{ style: { fontFamily: FONT, fontSize: '0.9rem', color: '#71717a', margin: 0 } }}
        >
          Start your free workspace today
        </Box>
      </SpaceBetween>

      {error !== null && (
        <Alert type="error" dismissible onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Social buttons — 2 columns */}
      <Box
        tagOverride="div"
        nativeAttributes={{ style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' } }}
      >
        <Button
          variant="normal"
          fullWidth
          iconUrl={GOOGLE_ICON}
          iconAlt="Google"
          loading={googleLoading}
          disabled={facebookLoading || isSubmitting}
          onClick={() => { void handleGoogleSignIn() }}
        >
          Google
        </Button>
        <Button
          variant="normal"
          fullWidth
          iconUrl={FACEBOOK_ICON}
          iconAlt="Facebook"
          loading={facebookLoading}
          disabled={googleLoading || isSubmitting}
          onClick={() => { void handleFacebookSignIn() }}
        >
          Facebook
        </Button>
      </Box>

      {/* OR divider */}
      <Box tagOverride="div" nativeAttributes={{ style: { display: 'flex', alignItems: 'center', gap: '12px' } }}>
        <Box tagOverride="div" nativeAttributes={{ style: { flex: 1, height: '1px', background: '#e5e5e5' } }} />
        <Box
          tagOverride="span"
          nativeAttributes={{ style: { fontFamily: FONT, fontSize: '0.75rem', color: '#a3a3a3', fontWeight: 500, letterSpacing: '0.06em' } }}
        >
          or
        </Box>
        <Box tagOverride="div" nativeAttributes={{ style: { flex: 1, height: '1px', background: '#e5e5e5' } }} />
      </Box>

      {/* Registration form */}
      <SpaceBetween direction="vertical" size="s">
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <FormField label="Full name" errorText={errors.name?.message}>
              <Input
                value={field.value}
                onChange={({ detail }) => field.onChange(detail.value)}
                placeholder="Jane Smith"
              />
            </FormField>
          )}
        />
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <FormField label="Email" errorText={errors.email?.message}>
              <Input
                type="email"
                value={field.value}
                onChange={({ detail }) => field.onChange(detail.value)}
                placeholder="you@agency.com"
              />
            </FormField>
          )}
        />
        <Controller
          name="password"
          control={control}
          render={({ field }) => (
            <FormField label="Password" errorText={errors.password?.message}>
              <Input
                type="password"
                value={field.value}
                onChange={({ detail }) => field.onChange(detail.value)}
                placeholder="Min. 6 characters"
              />
            </FormField>
          )}
        />
        <Controller
          name="confirmPassword"
          control={control}
          render={({ field }) => (
            <FormField label="Confirm password" errorText={errors.confirmPassword?.message}>
              <Input
                type="password"
                value={field.value}
                onChange={({ detail }) => field.onChange(detail.value)}
                placeholder="Repeat your password"
              />
            </FormField>
          )}
        />
        <Checkbox
          checked={agreedToTerms}
          onChange={({ detail }) => setAgreedToTerms(detail.checked)}
        >
          I agree to the{' '}
          <Link href="/terms" external>Terms & Privacy</Link>
        </Checkbox>
        <Button
          variant="primary"
          fullWidth
          loading={isSubmitting}
          disabled={socialLoading || !agreedToTerms}
          onClick={() => { handleSubmit(onSubmit)().catch(() => undefined) }}
        >
          Get started
        </Button>
      </SpaceBetween>

      {/* Sign-in link */}
      <Box tagOverride="div" nativeAttributes={{ style: { textAlign: 'center' } }}>
        <Box
          tagOverride="span"
          nativeAttributes={{ style: { fontFamily: FONT, fontSize: '0.875rem', color: '#71717a' } }}
        >
          Already have an account?{' '}
        </Box>
        <Link onFollow={onSwitchMode}>Sign in</Link>
      </Box>
    </SpaceBetween>
  )
}

// ── AuthPage (merged) ───────────────────────────────────────
export default function AuthPage() {
  const [mode, setMode] = useState<'login' | 'register'>('login')
  const [error, setError] = useState<string | null>(null)
  const isMobile = useMediaQuery('(max-width: 767px)')

  useEffect(() => {
    const style = document.createElement('style')
    style.id = 'auth-form-theme'
    style.textContent = AUTH_FORM_CSS
    document.head.appendChild(style)
    return () => style.remove()
  }, [])

  // Reset error when switching modes
  const handleSwitchMode = () => {
    setError(null)
    setMode((m) => (m === 'login' ? 'register' : 'login'))
  }

  return (
    <Box tagOverride="div" nativeAttributes={{ style: { display: 'flex', height: '100vh', fontFamily: FONT } }}>

      {/* ── LEFT: Form panel ───────────────────────────────────── */}
      <Box
        tagOverride="div"
        nativeAttributes={{
          'data-form-panel': 'true',
          style: {
            width: isMobile ? '100%' : '50%',
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            padding: isMobile ? '28px 20px' : '52px 64px',
            background: '#ffffff',
            borderRight: isMobile ? 'none' : '1px solid #f0f0f0',
            overflowY: 'auto',
          },
        }}
      >
        {/* Logo */}
        <Box
          tagOverride="div"
          nativeAttributes={{
            style: {
              width: isMobile ? '160px' : '220px',
              height: isMobile ? '40px' : '56px',
              backgroundImage: 'url(/logo-full.svg)',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'contain',
              backgroundPosition: 'left center',
              filter: 'invert(1) sepia(1) saturate(2) hue-rotate(110deg) brightness(0.9)',
            },
          }}
        />

        {/* Form area — centered vertically and horizontally */}
        <Box
          tagOverride="div"
          nativeAttributes={{ style: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' } }}
        >
          <Box tagOverride="div" nativeAttributes={{ style: { width: '100%', maxWidth: '380px' } }}>
            {mode === 'login' ? (
              <LoginForm onSwitchMode={handleSwitchMode} error={error} setError={setError} isMobile={isMobile} />
            ) : (
              <RegisterForm onSwitchMode={handleSwitchMode} error={error} setError={setError} isMobile={isMobile} />
            )}
          </Box>
        </Box>

        {/* Footer */}
        <Box
          tagOverride="p"
          nativeAttributes={{ style: { fontFamily: FONT, fontSize: '0.78rem', color: '#a3a3a3', margin: 0, marginTop: 'auto' } }}
        >
          © {new Date().getFullYear()} Sabar · Agent Architect
        </Box>
      </Box>

      {/* ── RIGHT: Dark brand showcase ──────────────────────────── */}
      {!isMobile && <Box
        tagOverride="div"
        nativeAttributes={{
          'data-showcase-panel': 'true',
          style: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '56px 52px',
            background: [
              `radial-gradient(ellipse at 70% 20%, rgba(16,185,129,0.16) 0%, transparent 52%)`,
              `radial-gradient(ellipse at 20% 85%, rgba(20,184,166,0.10) 0%, transparent 48%)`,
              B.bg,
            ].join(', '),
            position: 'relative',
            overflow: 'hidden',
          },
        }}
      >
        {/* Grid texture */}
        <Box
          tagOverride="div"
          nativeAttributes={{
            style: {
              position: 'absolute',
              inset: 0,
              backgroundImage: `linear-gradient(rgba(20,184,166,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(20,184,166,0.035) 1px, transparent 1px)`,
              backgroundSize: '40px 40px',
              pointerEvents: 'none',
            },
          }}
        />

        <Box tagOverride="div" nativeAttributes={{ style: { position: 'relative', zIndex: 1, width: '100%', maxWidth: '480px' } }}>
          <SpaceBetween direction="vertical" size="xl">

            <SpaceBetween direction="vertical" size="m">
              <Box
                tagOverride="h2"
                nativeAttributes={{
                  style: { fontFamily: FONT, fontSize: '2rem', fontWeight: 800, letterSpacing: '-0.03em', color: B.textPrimary, margin: 0, lineHeight: 1.15 },
                }}
              >
                {mode === 'login'
                  ? 'The AI platform agencies trust.'
                  : 'Join 200+ agencies already growing with Sabar.'}
              </Box>
              <Box
                tagOverride="p"
                nativeAttributes={{ style: { fontFamily: FONT, fontSize: '0.9rem', color: B.textSecondary, margin: 0, lineHeight: 1.6 } }}
              >
                Generate, schedule, and publish content across every client — in one workspace.
              </Box>
            </SpaceBetween>

            {/* Product preview card */}
            <Box
              tagOverride="div"
              nativeAttributes={{
                style: {
                  background: B.cardBg,
                  border: `1px solid ${B.border}`,
                  borderRadius: '16px',
                  padding: '24px',
                  boxShadow: `0 0 0 1px ${B.border}, 0 24px 48px rgba(0,0,0,0.4), 0 0 40px rgba(16,185,129,0.06)`,
                },
              }}
            >
              <SpaceBetween direction="vertical" size="m">
                <Box tagOverride="div" nativeAttributes={{ style: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' } }}>
                  <SpaceBetween direction="vertical" size="xxs">
                    <Box tagOverride="span" nativeAttributes={{ style: { fontFamily: FONT, fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.1em', color: B.textMuted, textTransform: 'uppercase' } }}>
                      Content Published
                    </Box>
                    <Box tagOverride="div" nativeAttributes={{ style: { display: 'flex', alignItems: 'baseline', gap: '8px' } }}>
                      <Box tagOverride="span" nativeAttributes={{ style: { fontFamily: FONT, fontSize: '2rem', fontWeight: 800, color: B.textPrimary, letterSpacing: '-0.04em' } }}>
                        12,847
                      </Box>
                      <Box
                        tagOverride="span"
                        nativeAttributes={{ style: { fontFamily: FONT, fontSize: '0.72rem', fontWeight: 600, color: B.emerald, background: `rgba(16,185,129,0.12)`, padding: '2px 7px', borderRadius: '20px' } }}
                      >
                        ↑ 24%
                      </Box>
                    </Box>
                  </SpaceBetween>
                  <Box tagOverride="div" nativeAttributes={{ style: { width: '32px', height: '32px', backgroundImage: 'url(/logo-icon.svg)', backgroundRepeat: 'no-repeat', backgroundSize: 'contain', opacity: 0.6 } }} />
                </Box>

                {/* Bar chart */}
                <Box tagOverride="div" nativeAttributes={{ style: { display: 'flex', alignItems: 'flex-end', gap: '4px', height: '52px' } }}>
                  {CHART_BARS.map((h, i) => (
                    <Box
                      key={i}
                      tagOverride="div"
                      nativeAttributes={{
                        style: {
                          flex: 1,
                          height: `${h}%`,
                          borderRadius: '3px 3px 0 0',
                          background: i === CHART_BARS.length - 1
                            ? `linear-gradient(180deg, ${B.teal}, ${B.emerald})`
                            : `rgba(16,185,129,${0.18 + (h / 100) * 0.22})`,
                        },
                      }}
                    />
                  ))}
                </Box>

                <Box tagOverride="div" nativeAttributes={{ style: { height: '1px', background: B.border } }} />

                {/* Stats row */}
                <Box tagOverride="div" nativeAttributes={{ style: { display: 'flex' } }}>
                  {[
                    { label: 'Total Reach', value: '98.2K' },
                    { label: 'Engagement', value: '4.7K' },
                    { label: 'Clients', value: '34' },
                  ].map((stat, i) => (
                    <Box
                      key={stat.label}
                      tagOverride="div"
                      nativeAttributes={{ style: { flex: 1, padding: '0 12px', borderLeft: i > 0 ? `1px solid ${B.border}` : 'none' } }}
                    >
                      <SpaceBetween direction="vertical" size="xxs">
                        <Box tagOverride="span" nativeAttributes={{ style: { fontFamily: FONT, fontSize: '1.05rem', fontWeight: 700, color: B.textPrimary, letterSpacing: '-0.02em' } }}>
                          {stat.value}
                        </Box>
                        <Box tagOverride="span" nativeAttributes={{ style: { fontFamily: FONT, fontSize: '0.7rem', color: B.textMuted } }}>
                          {stat.label}
                        </Box>
                      </SpaceBetween>
                    </Box>
                  ))}
                </Box>
              </SpaceBetween>
            </Box>

            {/* Social proof */}
            <Box
              tagOverride="div"
              nativeAttributes={{
                style: {
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '14px 18px',
                  background: `rgba(16,185,129,0.08)`,
                  border: `1px solid rgba(16,185,129,0.18)`,
                  borderRadius: '10px',
                },
              }}
            >
              <Box
                tagOverride="div"
                nativeAttributes={{
                  style: {
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${B.emerald}, ${B.teal})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  },
                }}
              >
                <Box tagOverride="span" nativeAttributes={{ style: { fontSize: '18px' } }}>★</Box>
              </Box>
              <SpaceBetween direction="vertical" size="xxs">
                <Box tagOverride="span" nativeAttributes={{ style: { fontFamily: FONT, fontSize: '0.82rem', fontWeight: 600, color: B.textPrimary } }}>
                  Trusted by 200+ agencies
                </Box>
                <Box tagOverride="span" nativeAttributes={{ style: { fontFamily: FONT, fontSize: '0.75rem', color: B.textMuted } }}>
                  "Cut our content production time by 70%"
                </Box>
              </SpaceBetween>
            </Box>

          </SpaceBetween>
        </Box>
      </Box>}

    </Box>
  )
}
